import {
  DataGridPro as MuiDataGridPro,
  gridClasses,
  type DataGridProProps,
  type GridValidRowModel,
} from '@mui/x-data-grid-pro';
import { LoadingTable } from '@/primitives';
import { blue, slate } from '@/lib/colours';
import {
  FOOTER_HEIGHT,
  HEADER_CONFIGURATION_HEIGHT,
  HEADER_HEIGHT,
  NAV_HEIGHT,
  PAGE_PADDING_TOP,
  TABLE_BOTTOM_PADDING,
} from '@/configs/layout';

export {
  GridActionsCellItem,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
  useGridApiRef,
  type GridColDef,
  type GridPaginationModel,
  type GridRowParams,
  type GridSortModel,
  type GridFilterModel,
  type GridRenderCellParams,
} from '@mui/x-data-grid-pro';

export type { GridToolbarContainerProps } from '@mui/x-data-grid-pro';

export function DataGridPro<T extends GridValidRowModel>(
  props: DataGridProProps<T> & {
    isConfiguration?: boolean;
    isFetching?: boolean;
    minusExtraHeight?: number;
  },
) {
  const { isConfiguration, isFetching, minusExtraHeight = 0 } = props;

  if (isFetching) {
    return <LoadingTable />;
  }

  const localeText = {
    ...props.localeText,
    // Support custom uppercase operator values used in filters
    filterOperatorCONTAINS: 'Contains',
    filterOperatorENDS_WITH: 'Ends With',
    filterOperatorEQUALS: 'Equals',
    filterOperatorGREATER_THAN: 'Greater Than',
    filterOperatorNOT_NULL: 'Is Not Empty',
    filterOperatorLESS_THAN: 'Less Than',
    filterOperatorNOT_EQUALS: 'Not Equals',
    filterOperatorSTARTS_WITH: 'Starts With',
  } as const;

  return (
    <MuiDataGridPro
      getRowClassName={(params) =>
        params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
      }
      localeText={localeText}
      {...props}
      sx={{
        height: `calc(100vh - ${NAV_HEIGHT}px - ${PAGE_PADDING_TOP}px - ${
          isConfiguration ? HEADER_CONFIGURATION_HEIGHT : HEADER_HEIGHT
        }px - ${TABLE_BOTTOM_PADDING}px - ${FOOTER_HEIGHT}px - ${minusExtraHeight}px)`,
        [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
          outline: 'transparent',
        },
        [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
          {
            outline: 'none',
          },
        '& .MuiDataGrid-selectedRowCount': {
          visibility: 'hidden',
        },
        '&.MuiDataGrid-root': {
          borderWidth: '0px',
          minHeight: '200px',
        },
        '.MuiDataGrid-topContainer::after': {
          backgroundColor: 'unset',
        },
        '.MuiDataGrid-columnHeader': {
          maxHeight: '52px',
        },
        '.MuiDataGrid-columnHeaderTitleContainerContent': {
          // Typography variant=titlesmall
          fontSize: '14px',
          fontWeight: '500',
          letterSpacing: '0.1px',
          color: `${slate[600]}`,
        },
        '.MuiDataGrid-columnHeader.pl': {
          paddingLeft: '26px',
        },
        '.MuiDataGrid-columnHeader--pinnedRight > .MuiDataGrid-columnSeparator':
          {
            display: 'none',
          },
        '.MuiDataGrid-columnHeader--withLeftBorder': {
          borderLeft: 'none',
        },
        '.MuiDataGrid-filler--pinnedRight': {
          borderLeft: 'none',
        },
        '.MuiDataGrid-row': {
          height: '44px',
        },
        '.MuiDataGrid-row.even .MuiDataGrid-cell--pinnedRight': {
          backgroundColor: slate[50],
        },
        '.MuiDataGrid-row.odd .MuiDataGrid-cell--pinnedRight': {
          backgroundColor: 'white',
        },
        '.MuiDataGrid-cell': {
          borderWidth: '0px',
          // Typography variant=bodysmall
          fontSize: '14px',
          fontWeight: '400',
          letterSpacing: '0.25px',
        },
        '.MuiDataGrid-withBorderColor': {
          borderColor: slate[200],
        },
        '.MuiDataGrid-footerContainer': {
          borderWidth: '0px',
        },
        '.even': {
          backgroundColor: slate[50],
        },
        '.MuiTablePagination-displayedRows': {
          minWidth: '120px',
          textAlign: 'center',
        },
        '.MuiTablePagination-root': {
          color: slate[500],
          '.MuiTablePagination-select': {
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: blue[600],
            borderRadius: '5px',
            padding: '8px 16px',
            paddingRight: '32px !important',
            color: blue[600],
            transition: 'none',
          },
          '.MuiTablePagination-input svg': {
            color: blue[600],
            right: '8px',
          },
          '.MuiTablePagination-actions .MuiButtonBase-root': {
            borderRadius: '5px',
            width: '48px',
            height: '48px',
            padding: '16px 16px',
            mr: 1,
            svg: {
              margin: '0px 6px',
            },

            '&:not(.Mui-disabled)': {
              color: blue[600],
              backgroundColor: blue[50],
            },
          },
        },
        ...props.sx,
      }}
    />
  );
}
